import React, { useState, useEffect, useRef } from 'react';
import {
    Row, Col, Card, Typography, Table, DatePicker, message, Select, Spin, Divider,
    Modal, Descriptions, Tag, Button, Dropdown, Menu
} from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import {
    DollarOutlined, CreditCardOutlined, TagsOutlined, RiseOutlined, 
    FileTextOutlined, CheckCircleOutlined, FileDoneOutlined, 
    ShoppingCartOutlined, LineChartOutlined, BarChartOutlined, PieChartOutlined,
    InfoCircleOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const SalesReport = () => {
    const shopId = localStorage.getItem('shopId');
    const [loading, setLoading] = useState(false);
    const [salesData, setSalesData] = useState([]);
    const [reportSummary, setReportSummary] = useState({});
    const [filters, setFilters] = useState({});
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedSale, setSelectedSale] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const chartRef = useRef(null);

    useEffect(() => {
        if (shopId) fetchSalesReport();
        else message.error('Shop ID not found. Please log in again.');
    }, [filters]);

    const fetchSalesReport = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/sales/all-sales/${shopId}`, {
                params: filters,
            });

            const sales = response.data.sales || [];
            const processedSales = sales.map(sale => {
                let totalProfit = 0;
                sale.sale_items?.forEach(item => {
                    const buying = parseFloat(item.buying_price) || 0;
                    const selling = parseFloat(item.selling_price || item.price) || 0;
                    const qty = parseFloat(item.quantity) || 0;
                    totalProfit += (selling - buying) * qty;
                });
                return { ...sale, real_profit: totalProfit };
            });

            setSalesData(processedSales.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
            setReportSummary(response.data.summary || {});
        } catch (error) {
            message.error('Failed to retrieve sales report.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (dates, dateStrings) => {
        if (dates) {
            setFilters({
                ...filters,
                start_date: dateStrings[0],
                end_date: dateStrings[1],
            });
        } else {
            const updated = { ...filters };
            delete updated.start_date;
            delete updated.end_date;
            setFilters(updated);
        }
    };

    const handleTypeFilterChange = (value) => {
        const updatedFilters = { ...filters };
        if (value) updatedFilters.type = value;
        else delete updatedFilters.type;
        setFilters(updatedFilters);
    };

    const showSaleDetails = (sale) => {
        setSelectedSale(sale);
        setModalVisible(true);
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(
            salesData.map(sale => ({
                'Date': moment(sale.created_at).format('YYYY-MM-DD HH:mm'),
                'Type': sale.type?.toUpperCase(),
                'Total Amount': `Tsh ${(parseFloat(sale.total) || 0).toFixed(2)}`,
                'Discount': `Tsh ${(parseFloat(sale.discount) || 0).toFixed(2)}`,
                'Profit': `Tsh ${(parseFloat(sale.real_profit) || 0).toFixed(2)}`,
                'Status': sale.status?.toUpperCase(),
                'Products Count': sale.sale_items?.length || 0
            }))
        );
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");
        
        // Add summary sheet
        const summarySheet = XLSX.utils.json_to_sheet([
            { 'Metric': 'Total Revenue', 'Value': `Tsh ${(reportSummary.totalRevenue || 0).toFixed(2)}` },
            { 'Metric': 'Processed Invoices', 'Value': `Tsh ${(reportSummary.processedPaidAmount || 0).toFixed(2)}` },
            { 'Metric': 'Invoice Debits', 'Value': `Tsh ${(reportSummary.remainingInInvoices || 0).toFixed(2)}` },
            { 'Metric': 'Discounts Given', 'Value': `Tsh ${(reportSummary.totalDiscount || 0).toFixed(2)}` },
            { 'Metric': 'Total Profit', 'Value': `Tsh ${(reportSummary.realProfit || 0).toFixed(2)}` },
            { 'Metric': 'Total Invoices', 'Value': reportSummary.invoiceCount || 0 },
            { 'Metric': 'Total Receipts', 'Value': reportSummary.receiptCount || 0 }
        ]);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
        
        XLSX.writeFile(workbook, `Sales_Report_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        
        // Title
        doc.setFontSize(18);
        doc.text('Sales Report', 105, 15, { align: 'center' });
        
        // Date range
        doc.setFontSize(12);
        doc.text(
            `Date Range: ${filters.start_date ? moment(filters.start_date).format('YYYY-MM-DD') : 'All time'} to ${filters.end_date ? moment(filters.end_date).format('YYYY-MM-DD') : 'Present'}`,
            105, 25, { align: 'center' }
        );
        
        // Summary section
        doc.setFontSize(14);
        doc.text('Summary', 14, 35);
        
        doc.autoTable({
            startY: 40,
            head: [['Metric', 'Value']],
            body: [
                ['Total Revenue', `Tsh ${(reportSummary.totalRevenue || 0).toFixed(2)}`],
                ['Processed Invoices', `Tsh ${(reportSummary.processedPaidAmount || 0).toFixed(2)}`],
                ['Invoice Debits', `Tsh ${(reportSummary.remainingInInvoices || 0).toFixed(2)}`],
                ['Discounts Given', `Tsh ${(reportSummary.totalDiscount || 0).toFixed(2)}`],
                ['Total Profit', `Tsh ${(reportSummary.realProfit || 0).toFixed(2)}`],
                ['Total Invoices', reportSummary.invoiceCount || 0],
                ['Total Receipts', reportSummary.receiptCount || 0]
            ],
            theme: 'grid',
            headStyles: { fillColor: [74, 107, 223] }
        });
        
        // Sales data section
        doc.setFontSize(14);
        doc.text('Sales Transactions', 14, doc.autoTable.previous.finalY + 15);
        
        doc.autoTable({
            startY: doc.autoTable.previous.finalY + 20,
            head: [['Date', 'Type', 'Total', 'Discount', 'Profit', 'Status']],
            body: salesData.map(sale => [
                moment(sale.created_at).format('YYYY-MM-DD HH:mm'),
                sale.type?.toUpperCase(),
                `Tsh ${(parseFloat(sale.total) || 0).toFixed(2)}`,
                `Tsh ${(parseFloat(sale.discount) || 0).toFixed(2)}`,
                `Tsh ${(parseFloat(sale.real_profit) || 0).toFixed(2)}`,
                sale.status?.toUpperCase()
            ]),
            theme: 'grid',
            headStyles: { fillColor: [74, 107, 223] }
        });
        
        doc.save(`Sales_Report_${moment().format('YYYYMMDD_HHmmss')}.pdf`);
    };

    const typeColorMap = {
        invoice: '#1890ff',
        receipt: '#52c41a',
    };

    const salesColumns = [
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm'),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <span style={{ color: typeColorMap[type] || '#555', fontWeight: 600 }}>
                    {type?.toUpperCase()}
                </span>
            ),
            filters: [
                { text: 'Invoice', value: 'invoice' },
                { text: 'Receipt', value: 'receipt' },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: 'Products',
            dataIndex: 'sale_items',
            key: 'products',
            render: (items, record) => (
                <Button 
                    type="link" 
                    onClick={() => showSaleDetails(record)}
                    style={{ padding: 0 }}
                >
                    View Products ({items?.length || 0})
                </Button>
            ),
        },
        {
            title: 'Total Amount',
            dataIndex: 'total',
            key: 'total',
            render: (value) => <Text strong> Tsh {(parseFloat(value) || 0).toFixed(2)}</Text>,
            sorter: (a, b) => parseFloat(a.total) - parseFloat(b.total),
        },
        {
            title: 'Discount',
            dataIndex: 'discount',
            key: 'discount',
            render: (value) => <Text type="secondary">Tsh {(parseFloat(value) || 0).toFixed(2)}</Text>,
        },
        {
            title: 'Profit',
            dataIndex: 'real_profit',
            key: 'real_profit',
            render: (value) => (
                <Text style={{ color: parseFloat(value) >= 0 ? '#3f8600' : '#cf1322' }}>
                    Tsh {(parseFloat(value) || 0).toFixed(2)}
                </Text>
            ),
            sorter: (a, b) => parseFloat(a.real_profit) - parseFloat(b.real_profit),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <span 
                    style={{ 
                        color: status === 'paid' ? '#3f8600' : '#cf1322', 
                        fontWeight: 600,
                        padding: '4px 8px',
                        borderRadius: 4,
                        background: status === 'paid' ? '#f6ffed' : '#fff1f0'
                    }}
                >
                    {status?.toUpperCase()}
                </span>
            ),
            filters: [
                { text: 'Paid', value: 'paid' },
                { text: 'Unpaid', value: 'unpaid' },
            ],
            onFilter: (value, record) => record.status === value,
        },
    ];

    const statCards = [
        { 
            title: 'Total Revenue', 
            value: reportSummary.totalRevenue, 
            icon: <DollarOutlined />
        },
        { 
            title: 'Processed Invoices', 
            value: reportSummary.processedPaidAmount, 
            icon: <FileTextOutlined />
        },
        { 
            title: 'Invoice Debits', 
            value: reportSummary.remainingInInvoices, 
            icon: <CreditCardOutlined />
        },
        { 
            title: 'Discounts Given', 
            value: reportSummary.totalDiscount, 
            icon: <TagsOutlined />
        },
        { 
            title: 'Total Profit', 
            value: reportSummary.realProfit, 
            icon: <RiseOutlined />
        },
        { 
            title: 'Total Invoices', 
            value: reportSummary.invoiceCount, 
            icon: <FileDoneOutlined />
        },
        { 
            title: 'Total Receipts', 
            value: reportSummary.receiptCount, 
            icon: <CheckCircleOutlined />
        },
    ];

    const getCardColor = (title) => {
        const text = title.toLowerCase();
        if (text.includes('revenue')) return { bg: '#f6f7fe', color: '#4a6bdf' };
        if (text.includes('profit')) return { bg: '#f0f9eb', color: '#67c23a' };
        if (text.includes('discount')) return { bg: '#fef0f0', color: '#f56c6c' };
        if (text.includes('receipt')) return { bg: '#f0f9ff', color: '#409eff' };
        if (text.includes('debit')) return { bg: '#fff8e6', color: '#e6a23c' };
        if (text.includes('invoice')) return { bg: '#f9f0ff', color: '#9254de' };
        return { bg: '#f6f6f6', color: '#333' };
    };

    const pieChartData = [
        { name: 'Revenue', value: reportSummary.totalRevenue || 0, color: '#4a6bdf' },
        { name: 'Profit', value: reportSummary.realProfit || 0, color: '#67c23a' },
        { name: 'Discounts', value: reportSummary.totalDiscount || 0, color: '#f56c6c' },
        { name: 'Processsed Invoices', value: reportSummary.processedPaidAmount || 0, color: '#409eff' },
        { name: 'Unpaid Invoices', value: reportSummary.remainingInInvoices || 0, color: '#e6a23c' },
    ];

    const barChartData = [
        { name: 'Revenue', value: reportSummary.totalRevenue || 0 },
        { name: 'Profit', value: reportSummary.realProfit || 0 },
        { name: 'Discounts', value: reportSummary.totalDiscount || 0 },
        { name: 'Processsed', value: reportSummary.processedPaidAmount || 0 },
        { name: 'Unpaid', value: reportSummary.remainingInInvoices || 0 },
    ];

    const formatCurrency = (value) => {
        return `Tsh ${(parseFloat(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const renderTrendIndicator = (trend) => {
        switch(trend) {
            case 'up':
                return <RiseOutlined style={{ color: '#52c41a', marginLeft: 4 }} />;
            case 'down':
                return <RiseOutlined style={{ color: '#f5222d', transform: 'rotate(180deg)', marginLeft: 4 }} />;
            default:
                return <span style={{ marginLeft: 4 }}>-</span>;
        }
    };

    const calculateItemProfit = (item) => {
        const buying = parseFloat(item.buying_price) || 0;
        const selling = parseFloat(item.selling_price || item.price) || 0;
        return (selling - buying) * (parseFloat(item.quantity) || 0);
    };

    const exportMenu = (
        <Menu>
            <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={exportToExcel}>
                Export to Excel
            </Menu.Item>
            <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={exportToPDF}>
                Export to PDF
            </Menu.Item>
        </Menu>
    );

    return (
        <div style={{ padding: '24px', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0 }}>
                    <LineChartOutlined style={{ marginRight: 12, color: '#4a6bdf' }} />
                    Sales
                </Title>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <RangePicker 
                        onChange={handleDateRangeChange}
                        style={{ marginRight: 8 }}
                        ranges={{
                            'Today': [moment().startOf('day'), moment().endOf('day')],
                            'This Week': [moment().startOf('week'), moment().endOf('week')],
                            'This Month': [moment().startOf('month'), moment().endOf('month')],
                        }}
                        format="YYYY-MM-DD"
                    />
                    <Select
                        allowClear
                        placeholder="Filter by type"
                        style={{ width: 150, marginRight: 8 }}
                        onChange={handleTypeFilterChange}
                    >
                        <Option value="invoice">Invoice</Option>
                        <Option value="receipt">Receipt</Option>
                    </Select>
                    <Dropdown overlay={exportMenu} placement="bottomRight">
                        <Button type="primary" icon={<DownloadOutlined />}>
                            Export
                        </Button>
                    </Dropdown>
                </div>
            </div>

            <Spin spinning={loading}>
                <Row gutter={[24, 24]}>
                    {statCards.map((stat, index) => (
                        <Col key={index} xs={24} sm={12} md={8} lg={6} xl={6}>
                            <Card 
                                bordered={false} 
                                style={{ 
                                    borderRadius: 12,
                                    background: getCardColor(stat.title).bg,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    height: '100%'
                                }}
                                bodyStyle={{ padding: 16 }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <div style={{ 
                                        background: getCardColor(stat.title).color + '20',
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: 12
                                    }}>
                                        {React.cloneElement(stat.icon, { 
                                            style: { 
                                                color: getCardColor(stat.title).color,
                                                fontSize: 18
                                            } 
                                        })}
                                    </div>
                                    <Text strong style={{ color: getCardColor(stat.title).color }}>
                                        {stat.title}
                                    </Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                    <Title level={3} style={{ margin: 0 }}>
                                        {stat.title.includes('Invoices') || stat.title.includes('Receipts') 
                                            ? stat.value || 0 
                                            : formatCurrency(stat.value)}
                                    </Title>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {stat.change}
                                        </Text>
                                        {renderTrendIndicator(stat.trend)}
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Divider />

                <Card 
                    bordered={false} 
                    style={{ borderRadius: 12, marginBottom: 24 }}
                    bodyStyle={{ padding: 0 }}
                >
                    <div style={{ 
                        display: 'flex', 
                        borderBottom: '1px solid #f0f0f0',
                        padding: '0 24px'
                    }}>
                        <div 
                            style={{
                                padding: '16px 24px',
                                borderBottom: activeTab === 'overview' ? '2px solid #4a6bdf' : 'none',
                                cursor: 'pointer',
                                fontWeight: activeTab === 'overview' ? 600 : 400
                            }}
                            onClick={() => setActiveTab('overview')}
                        >
                            <PieChartOutlined style={{ marginRight: 8 }} />
                            Overview
                        </div>
                        <div 
                            style={{
                                padding: '16px 24px',
                                borderBottom: activeTab === 'comparison' ? '2px solid #4a6bdf' : 'none',
                                cursor: 'pointer',
                                fontWeight: activeTab === 'comparison' ? 600 : 400
                            }}
                            onClick={() => setActiveTab('comparison')}
                        >
                            <BarChartOutlined style={{ marginRight: 8 }} />
                            Comparison
                        </div>
                    </div>

                    <div style={{ padding: 24 }}>
                        {activeTab === 'overview' ? (
                            <ResponsiveContainer width="100%" height={400}>
                                <PieChart>
                                    <Pie
                                        data={pieChartData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={120}
                                        innerRadius={60}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        labelLine={false}
                                    >
                                        {pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        formatter={(value) => [formatCurrency(value), 'Amount']}
                                    />
                                    <Legend 
                                        layout="vertical" 
                                        verticalAlign="middle" 
                                        align="right"
                                        wrapperStyle={{ paddingLeft: 24 }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={barChartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" />
                                    <YAxis 
                                        tickFormatter={(value) => `Tsh ${(value / 1000).toFixed(0)}K`}
                                    />
                                    <Tooltip 
                                        formatter={(value) => [formatCurrency(value), 'Amount']}
                                        labelFormatter={(label) => label}
                                    />
                                    <Legend />
                                    <Bar 
                                        dataKey="value" 
                                        name="Amount" 
                                        fill="#4a6bdf" 
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </Card>

                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <ShoppingCartOutlined style={{ marginRight: 8, color: '#4a6bdf' }} />
                            <span>Sales Transactions</span>
                        </div>
                    }
                    bordered={false}
                    style={{ borderRadius: 12 }}
                    bodyStyle={{ padding: 0 }}
                >
                    <Table
                        dataSource={salesData}
                        columns={salesColumns}
                        rowKey="id"
                        pagination={{ 
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
                        }}
                        loading={loading}
                        scroll={{ x: true }}
                        style={{ borderRadius: 12 }}
                        summary={() => (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={3}>
                                        <Text strong>Total</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                        <Text strong>{formatCurrency(reportSummary.totalRevenue)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                        <Text strong>{formatCurrency(reportSummary.totalDiscount)}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                        <Text strong style={{ color: '#3f8600' }}>
                                            {formatCurrency(reportSummary.realProfit)}
                                        </Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                </Card>
            </Spin>

            {/* Sale Details Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <InfoCircleOutlined style={{ marginRight: 8, color: '#4a6bdf' }} />
                        <span>Sale Details</span>
                    </div>
                }
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedSale && (
                    <div>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Sale ID">{selectedSale.id}</Descriptions.Item>
                            <Descriptions.Item label="Date">
                                {moment(selectedSale.created_at).format('YYYY-MM-DD HH:mm')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Type">
                                <Tag color={typeColorMap[selectedSale.type]}>
                                    {selectedSale.type?.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={selectedSale.status === 'paid' ? 'green' : 'red'}>
                                    {selectedSale.status?.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Amount">
                                <Text strong>{formatCurrency(selectedSale.total)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Discount">
                                <Text type="secondary">{formatCurrency(selectedSale.discount)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Profit">
                                <Text style={{ color: parseFloat(selectedSale.real_profit) >= 0 ? '#3f8600' : '#cf1322' }}>
                                    {formatCurrency(selectedSale.real_profit)}
                                </Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">Products Sold</Divider>

                        <Table
                            dataSource={selectedSale.sale_items || []}
                            columns={[
                                {
                                    title: 'Product',
                                    dataIndex: 'product_name',
                                    key: 'product',
                                    render: (text) => <Text strong>{text}</Text>,
                                },
                                {
                                    title: 'Quantity',
                                    dataIndex: 'quantity',
                                    key: 'quantity',
                                    render: (text) => <Text>x{text}</Text>,
                                },
                                {
                                    title: 'Unit Price',
                                    dataIndex: 'price',
                                    key: 'price',
                                    render: (text) => formatCurrency(text),
                                },
                                {
                                    title: 'Buying Price',
                                    dataIndex: 'buying_price',
                                    key: 'buying_price',
                                    render: (text) => formatCurrency(text),
                                },
                                {
                                    title: 'Profit',
                                    key: 'profit',
                                    render: (_, record) => (
                                        <Text style={{ color: calculateItemProfit(record) >= 0 ? '#3f8600' : '#cf1322' }}>
                                            {formatCurrency(calculateItemProfit(record))}
                                        </Text>
                                    ),
                                },
                                {
                                    title: 'Total',
                                    key: 'total',
                                    render: (_, record) => formatCurrency((parseFloat(record.price) || 0) * (parseFloat(record.quantity) || 0)),
                                },
                            ]}
                            pagination={false}
                            size="small"
                            rowKey="id"
                            summary={() => (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={4}>
                                            <Text strong>Total</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                            <Text strong style={{ color: '#3f8600' }}>
                                                {formatCurrency(selectedSale.sale_items?.reduce((sum, item) => sum + calculateItemProfit(item), 0))}
                                            </Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2}>
                                            <Text strong>{formatCurrency(selectedSale.total)}</Text>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SalesReport;